import React, { useState, useMemo } from "react"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Typography from "aldoo-ra/Typography"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"

// Flatten a nested object into dot-notation key-value pairs
const flatten = (obj, prefix = "", result = {}) => {
  if (!obj || typeof obj !== "object") return result
  for (const [key, val] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key
    if (
      val !== null &&
      typeof val === "object" &&
      !Array.isArray(val) &&
      !(val instanceof Date)
    ) {
      flatten(val, fullKey, result)
    } else {
      result[fullKey] = val
    }
  }
  return result
}

const renderValue = (val) => {
  if (val === null || val === undefined) return <span className="text-admin_text_dim italic">null</span>
  if (typeof val === "boolean")
    return <span className="text-admin_primary font-mono">{String(val)}</span>
  if (Array.isArray(val))
    return <span className="text-admin_text_secondary font-mono">[{val.length} items]</span>
  return <span className="break-all">{String(val)}</span>
}

export default function ObjectBrowser({ item, title = "Item Details", onClose }) {
  const [search, setSearch] = useState("")

  const flat = useMemo(() => (item ? flatten(item) : {}), [item])

  const entries = useMemo(() => {
    const all = Object.entries(flat)
    if (!search.trim()) return all
    const term = search.toLowerCase()
    return all.filter(
      ([k, v]) =>
        k.toLowerCase().includes(term) || String(v).toLowerCase().includes(term)
    )
  }, [flat, search])

  return (
    <Modal
      isOpen={!!item}
      onClose={onClose}
      title={title}
      variant="admin_drawer_right"
      closeOnClickOutside
    >
      <div>
        <div className="sticky top-0 z-10 px-4 py-3 border-b border-admin_bordered bg-admin_paper">
          <Input
            variant="admin"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search keys or values..."
            autoFocus
            className="w-full"
            icon={<MagnifyingGlassIcon className="h-5 w-5 text-admin_text_secondary" />}
            showClearButton={search.length > 0}
            onClear={() => setSearch("")}
          />
        </div>

        <div>
          {entries.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <Typography variant="admin_body" className="text-admin_text_dim">
                {search ? "No matching fields" : "Empty object"}
              </Typography>
            </div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {entries.map(([key, val]) => (
                  <tr
                    key={key}
                    className="border-b border-admin_bordered/40 hover:bg-admin_canvas/50"
                  >
                    <td className="px-4 py-2 font-mono text-admin_text_secondary align-top w-[45%] truncate max-w-0">
                      <span title={key}>{key}</span>
                    </td>
                    <td className="px-4 py-2 text-admin_text align-top">
                      {renderValue(val)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="sticky bottom-0 px-4 py-2 border-t border-admin_bordered bg-admin_paper text-xs text-admin_text_dim">
          {entries.length} field{entries.length !== 1 ? "s" : ""}
          {search && ` matching "${search}"`}
        </div>
      </div>
    </Modal>
  )
}
